import {
  Box,
  Button,
  Divider,
  Flex,
  type IconProps,
  Text,
  type TextProps,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { ExpenseBudgetItemTypeEnum } from "@src/__generated__/graphql";
import { ProjectTypeEnum } from "@src/__generated__/urql-graphql";
import type { OurWorkOverviewModel } from "@src/components/modules/projects/detail/models";
import { ProgressBarOurWorkSpend } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent } from "react";
import { useStore as useProjectStore } from "../Store";

type ProgressProps = {
  item: OurWorkOverviewModel["budgetItems"][0];
  onSpentBudgetClick?: () => void;
  visibleBudgetItemIds: string[];
};

function getIconColor(value: number): IconProps["color"] {
  if (value > 0) {
    return "jade.700";
  }
  return "gray.500";
}

function getTextColor(value: number): TextProps["color"] {
  if (value > 0) {
    return "initial";
  }
  return "gray.500";
}

export const Progress: FunctionComponent<ProgressProps> = observer(
  function Progress({ item, onSpentBudgetClick, visibleBudgetItemIds }) {
    const { workspaceStore } = useStore();
    const store = useProjectStore();

    const trackedTime = workspaceStore.settings?.formatTimeTrackingRateValue(
      item.stats.spent_time,
      1,
    );
    // TODO: Finish after API will be done
    // const allocatedTime = workspaceStore.settings?.formatTimeTrackingRateValue(0, 0)
    const budgetedTime = workspaceStore.settings?.formatTimeTrackingRateValue(
      item.stats.total_time,
      1,
    );

    return (
      <React.Fragment>
        <Flex align="center" gap="2" mb="1.5">
          {!store.project?.isTemplate && (
            <>
              <Flex direction="column">
                <Flex align="center" gap="1">
                  <Icon name="clock" color={getIconColor(trackedTime ?? 0)} />
                  <Text
                    color={getTextColor(trackedTime ?? 0)}
                    fontWeight="medium"
                  >
                    {workspaceStore.settings?.timeTrackingRateValueReadable(
                      trackedTime ?? 0,
                      t`h`,
                    )}
                  </Text>
                </Flex>
                <Box minW="20">
                  <Button
                    color="gray.600"
                    fontSize="xs"
                    fontWeight="medium"
                    textAlign="left"
                    whiteSpace="nowrap"
                    leftIcon={
                      item.expenseStats.length ? (
                        <Icon name="currency-euro-circle" />
                      ) : undefined
                    }
                    onClick={() => onSpentBudgetClick?.()}
                    variant="link"
                  >
                    {currency.formatByWorkspace(item.stats.spent_budget)}
                  </Button>
                </Box>
              </Flex>
              {/* TODO: Finish after API will be done */}
              {/* <Divider h="7" borderColor="gray.400" orientation="vertical" />
        <Flex direction="column">
          <Text color={getTextColor(allocatedTime ?? 0)} fontWeight="medium">
            {allocatedTime}h
          </Text>
          <Text
            minW="20"
            color="gray.600"
            fontSize="xs"
            fontWeight="medium"
            textAlign="left"
            whiteSpace="nowrap"
          >
            {currency.formatByWorkspace(0, globalStore)}
          </Text>
        </Flex> */}
              <Divider h="7" borderColor="gray.400" orientation="vertical" />
            </>
          )}
          <Flex direction="column">
            <Text color={getTextColor(budgetedTime ?? 0)} fontWeight="medium">
              {workspaceStore.settings?.timeTrackingRateValueReadable(
                budgetedTime ?? 0,
                t`h`,
              )}
            </Text>
            <Text
              minW="20"
              color="gray.600"
              fontSize="xs"
              fontWeight="medium"
              textAlign="left"
              whiteSpace="nowrap"
            >
              {currency.formatByWorkspace(item.stats.total_budget)}
            </Text>
          </Flex>

          <Box ml="auto" pl="2">
            <Button
              colorScheme="blue"
              onClick={() => {
                store.plannedExpensesModalStore.modalState.onOpen({
                  item: {
                    id: item.id,
                    title: item.title,
                    budgetItemType: ExpenseBudgetItemTypeEnum.OurWorkBudgetItem,
                    targetClientAmount: item.stats.total_budget,
                  },
                  visibleBudgetItemIds,
                  onSaved: ({ budgetItemIds, hasPlannedExpenses }) => {
                    store.updateOurWorkBudgetItemsHasPlannedExpenses(
                      budgetItemIds,
                      hasPlannedExpenses,
                    );
                  },
                });
              }}
              rightIcon={
                item.hasPlannedExpenses ? (
                  <Icon boxSize="4" color="green.500" name="check-circle" />
                ) : undefined
              }
              size="xs"
              variant="link"
            >
              <Trans>Planned expenses</Trans>
            </Button>
          </Box>
        </Flex>
        {!store.project?.isTemplate && (
          <ProgressBarOurWorkSpend
            total_budget={item.stats.total_budget}
            spent_budget={item.stats.spent_budget}
            credit={item.stats.credit}
            isTimeAndMaterials={
              store.project?.type === ProjectTypeEnum.TimeMaterials
            }
          />
        )}
      </React.Fragment>
    );
  },
);
